<script setup>
import { computed, nextTick, onMounted } from 'vue';
import moment from 'moment';
import { useBus } from '/app/composables/bus';
import { useFormContext } from '/app/composables/form';
import NRadioGroup from '/app/components/form/RadioGroup.vue';
import NRadio from '/app/components/form/Radio.vue';
import NDatepicker from '/app/components/form/Datepicker.vue';

const { on } = useBus();

const props = defineProps({
    hideTips: Boolean,
    days: Number,
    disabled: Boolean,
});

const form = useFormContext();
const defaultDays = 30;
const today = computed(() => moment().startOf('day'));
const minEndDate = computed(() => {
    const startDate = form.fields.challenge.duration_start;
    return startDate
        ? moment(startDate).startOf('day').format('YYYY-MM-DD')
        : today.value.format('YYYY-MM-DD');
});

const currentDays = computed(() => {
    if (form.fields.challenge.duration_end) {
        const days = moment(form.fields.challenge.duration_end).diff(
            moment(form.fields.challenge.duration_start),
            'days'
        );

        return (d => (d < 20 ? 15 : d < 45 ? 30 : 90))(days);
    }

    return defaultDays;
});

const duration = computed({
    get() {
        return currentDays.value;
    },
    set(value) {
        form.fields.challenge.duration_end = value;
    },
});

const normalizeStartDate = (value) => moment(value).startOf('day').toISOString();
const normalizeEndDate = (value) => moment(value).endOf('day').toISOString();
const getMinEndMoment = () => {
    const startDate = form.fields.challenge.duration_start
        ? moment(form.fields.challenge.duration_start).startOf('day')
        : today.value;

    return moment.max(today.value, startDate);
};
const clampDateToToday = (value, mode = 'start') => {
    const selected = moment(value);

    const minAllowed = mode === 'end' ? getMinEndMoment() : today.value;

    if (selected.isBefore(minAllowed, 'day')) {
        return mode === 'end'
            ? minAllowed.clone().endOf('day').toISOString()
            : minAllowed.toISOString();
    }

    return mode === 'end'
        ? normalizeEndDate(selected)
        : normalizeStartDate(selected);
};

const setDuration = (days) => {
    const interval = normalizeEndDate(moment().add(days, 'days'));
    const startDate = today.value.toISOString();

    nextTick(() => {
        form.fields.challenge.duration_start = startDate;
        form.fields.challenge.duration_end = interval;
    });
};

const setDate = (value, field) => {
    const dateValue = field === 'duration_end'
        ? clampDateToToday(value, 'end')
        : clampDateToToday(value, 'start');

    form.fields.challenge[field] = dateValue;
};

on('challenge:creation-flow:reseted', () => setDuration(defaultDays));
on('challenge:creation-flow:recovered', () => setDuration(currentDays.value));
on('challenge:creation-flow:edition-started', () => setDuration(currentDays.value));

onMounted(() => {
    if (props.days) {
        setDuration(props.days);
    }
});
</script>

<template>
    <div>
        <div class="challenge-section-heading">
            <h2 class="challenge-section-heading__title">
                <b>How long will your challenge run?</b>
            </h2>
        </div>
        <div class="form-section-wrapper flex flex-col md:flex-row gap-4 mb-4" :class="{ disabled: disabled || form.busy }">
            <div class="form-group is-boxed md:min-w-[13rem]">
                <span class="form-group__label">Choose a Default</span>
                <div class="form-group__box">
                    <NRadioGroup :modelValue="duration" name="duration" @change="setDuration">
                        <NRadio label="15 Days" class="my-1" :modelValue="15" />
                        <NRadio label="30 Days" class="my-1" :modelValue="30" />
                        <NRadio label="90 Days" class="my-1" :modelValue="90" />
                    </NRadioGroup>
                </div>
            </div>
            <div class="form-group is-boxed min-w-[25rem]">
                <span class="form-group__label">Or set a custom Date and Time range</span>
                <div class="form-group__box">
                    <NDatepicker
                        :modelValue="form.fields.challenge.duration_start"
                        :min-date="today.format('YYYY-MM-DD')"
                        :end-time="$refs.duration_end"
                        name="duration_start"
                        label="Start Date:"
                        @change="setDate($event, 'duration_start')"
                    ></NDatepicker>
                    <NDatepicker
                        :modelValue="form.fields.challenge.duration_end"
                        :min-date="minEndDate"
                        ref="duration_end"
                        name="duration_end"
                        label="End Date:"
                        @change="setDate($event, 'duration_end')"
                    ></NDatepicker>
                </div>
            </div>
        </div>
        <div v-if="!hideTips" class="info-box mt-4">
            <span aria-hidden="true" class="info-box__icon icon-exclamation-circle-solid"></span>
            <p class="info-box__content">
                <b>Tip:</b>  Depending on your player’s engagement, optimal duration can vary: <b>a longer</b> duration can bring in more ideas and thus higher chances of quality. But <b>shorter</b> durations means that your challenges close and award winners frequently, so maybe you’ll get more engagement.
            </p>
        </div>
    </div>
</template>
